const pageStyle = {
  maxWidth: 800,
  margin: "0 auto",
  padding: "40px 20px",
  color: "var(--text)",
  lineHeight: 1.7,
  fontSize: 15,
} as const;

const sectionStyle = {
  marginBottom: 40,
} as const;

const h1Style = {
  fontSize: "2em",
  fontWeight: 700,
  marginBottom: 8,
} as const;

const h2Style = {
  fontSize: "1.4em",
  fontWeight: 600,
  marginTop: 32,
  marginBottom: 12,
  color: "var(--text)",
} as const;

const lastUpdated = "February 15, 2026";

const policyNavStyle = {
  display: "flex",
  gap: 24,
  padding: "0 0 24px",
  borderBottom: "1px solid var(--border)",
  marginBottom: 20,
  flexWrap: "wrap",
  fontSize: 14,
} as const;

const policyNavLinkStyle = {
  color: "var(--primary)",
  textDecoration: "none",
  fontWeight: 500,
} as const;

const PolicyNav = () => (
  <nav style={policyNavStyle}>
    <a href="/terms" style={policyNavLinkStyle}>Terms of Service</a>
    <a href="/privacy" style={policyNavLinkStyle}>Privacy Policy</a>
    <a href="/refund-policy" style={policyNavLinkStyle}>Refund Policy</a>
    <a href="/" style={{ ...policyNavLinkStyle, opacity: 0.7 }}>
      ← Back to Home
    </a>
  </nav>
);

export const TermsPage = () => (
  <div style={pageStyle}>
    <PolicyNav />
    <div style={sectionStyle}>
      <h1 style={h1Style}>Terms of Service</h1>
      <p style={{ opacity: 0.7, fontSize: 13 }}>Last updated: {lastUpdated}</p>

      <h2 style={h2Style}>1. Acceptance of Terms</h2>
      <p>
        By accessing or using Supergreen ("the Service"), you agree to be bound
        by these Terms of Service. If you do not agree, do not use the Service.
      </p>

      <h2 style={h2Style}>2. Description of Service</h2>
      <p>
        Supergreen provides an API-based platform that allows users to connect
        their WhatsApp and Telegram accounts for automation purposes. The
        Service enables sending and receiving messages, managing conversations,
        and integrating messaging workflows via a RESTful API.
      </p>

      <h2 style={h2Style}>3. Account Registration</h2>
      <p>
        You must provide a valid email address to create an account. You are
        responsible for maintaining the confidentiality of your account
        credentials and API tokens. You are responsible for all activities that
        occur under your account.
      </p>

      <h2 style={h2Style}>4. Acceptable Use</h2>
      <p>You agree not to use the Service to:</p>
      <ul>
        <li>
          Send spam, unsolicited messages, or bulk commercial communications
        </li>
        <li>Violate any applicable laws or regulations</li>
        <li>
          Violate the terms of service of third-party platforms (WhatsApp,
          Telegram, etc.)
        </li>
        <li>Harass, abuse, or harm others</li>
        <li>Distribute malware or engage in phishing</li>
        <li>Impersonate any person or entity</li>
        <li>
          Interfere with or disrupt the Service or servers connected to the
          Service
        </li>
      </ul>

      <h2 style={h2Style}>5. Pricing and Payment</h2>
      <p>
        The Service is billed at $10 per connected line per month. A free trial
        period is available for new users. Payments are processed through our
        payment partner, Stripe. All fees are non-refundable except as described
        in our{" "}
        <a href="/refund-policy" style={{ color: "var(--primary)" }}>
          Refund Policy
        </a>. We reserve the right to change pricing with 30 days notice.
      </p>

      <h2 style={h2Style}>6. Free Trial</h2>
      <p>
        New users may be eligible for a free trial period. At the end of the
        trial, your account will be billed at the standard rate unless you
        cancel before the trial ends. Trial eligibility is limited to one per
        user.
      </p>

      <h2 style={h2Style}>7. Cancellation</h2>
      <p>
        You may cancel your subscription at any time. Upon cancellation, your
        access will continue until the end of the current billing period. After
        that, connected lines will be disconnected.
      </p>

      <h2 style={h2Style}>8. Intellectual Property</h2>
      <p>
        The Service, including its software, design, and documentation, is owned
        by Supergreen. You retain ownership of any data you transmit through the
        Service.
      </p>

      <h2 style={h2Style}>9. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, Supergreen shall not be liable
        for any indirect, incidental, special, consequential, or punitive
        damages, or any loss of profits or revenues, whether incurred directly
        or indirectly. The Service is provided "as is" without warranties of any
        kind.
      </p>

      <h2 style={h2Style}>10. Service Availability</h2>
      <p>
        We strive to maintain high availability but do not guarantee
        uninterrupted service. The Service depends on third-party platforms
        (WhatsApp, Telegram) whose availability is outside our control. We are
        not liable for downtime caused by third-party services.
      </p>

      <h2 style={h2Style}>11. Modifications to Terms</h2>
      <p>
        We may update these Terms from time to time. We will notify users of
        material changes via email or through the Service. Continued use after
        changes constitutes acceptance.
      </p>

      <h2 style={h2Style}>12. Termination</h2>
      <p>
        We may suspend or terminate your access if you violate these Terms or
        engage in activity harmful to the Service or its users, with or without
        notice.
      </p>

      <h2 style={h2Style}>13. Contact</h2>
      <p>
        For questions about these Terms, contact us at{" "}
        <a
          href="mailto:support@supergreen.cc"
          style={{ color: "var(--primary)" }}
        >
          support@supergreen.cc
        </a>.
      </p>
    </div>
  </div>
);

export const PrivacyPage = () => (
  <div style={pageStyle}>
    <PolicyNav />
    <div style={sectionStyle}>
      <h1 style={h1Style}>Privacy Policy</h1>
      <p style={{ opacity: 0.7, fontSize: 13 }}>Last updated: {lastUpdated}</p>

      <h2 style={h2Style}>1. Information We Collect</h2>
      <p>We collect the following types of information:</p>
      <ul>
        <li>
          <strong>Account information:</strong>{" "}
          email address and WhatsApp/Telegram phone numbers you provide when
          registering
        </li>
        <li>
          <strong>Usage data:</strong>{" "}
          API call logs, connection status, and timestamps of activity
        </li>
        <li>
          <strong>Message data:</strong>{" "}
          messages sent and received through the Service are processed to
          deliver the Service functionality. We do not read or analyze message
          content for advertising or profiling purposes.
        </li>
        <li>
          <strong>Payment information:</strong>{" "}
          payment details are handled by our payment processor, Stripe. We do
          not store credit card numbers.
        </li>
      </ul>

      <h2 style={h2Style}>2. How We Use Your Information</h2>
      <p>We use your information to:</p>
      <ul>
        <li>Provide and maintain the Service</li>
        <li>Process payments and manage your subscription</li>
        <li>Send important service notifications</li>
        <li>Respond to support requests</li>
        <li>Improve the Service and fix bugs</li>
        <li>Prevent fraud and abuse</li>
      </ul>

      <h2 style={h2Style}>3. Data Sharing</h2>
      <p>
        We do not sell your personal information. We may share data with:
      </p>
      <ul>
        <li>
          <strong>Payment processors:</strong> Stripe, to process your payments
        </li>
        <li>
          <strong>Infrastructure providers:</strong>{" "}
          cloud hosting services necessary to operate the Service
        </li>
        <li>
          <strong>Legal requirements:</strong>{" "}
          if required by law or to protect our rights
        </li>
      </ul>

      <h2 style={h2Style}>4. Data Retention</h2>
      <p>
        We retain your data for as long as your account is active. Upon account
        deletion, we will remove your personal data within 30 days. Some data
        may be retained longer if required by law or for legitimate business
        purposes (e.g., billing records).
      </p>

      <h2 style={h2Style}>5. Data Security</h2>
      <p>
        We use industry-standard security measures to protect your data,
        including encrypted connections (TLS), secure authentication, and access
        controls. However, no method of electronic transmission or storage is
        100% secure.
      </p>

      <h2 style={h2Style}>6. Your Rights</h2>
      <p>You have the right to:</p>
      <ul>
        <li>Access the personal data we hold about you</li>
        <li>Request correction of inaccurate data</li>
        <li>Request deletion of your data</li>
        <li>Export your data in a portable format</li>
        <li>Withdraw consent at any time</li>
      </ul>
      <p>
        To exercise these rights, contact us at{" "}
        <a
          href="mailto:support@supergreen.cc"
          style={{ color: "var(--primary)" }}
        >
          support@supergreen.cc
        </a>.
      </p>

      <h2 style={h2Style}>7. Cookies</h2>
      <p>
        We use essential cookies and local storage for authentication and
        session management. We do not use tracking cookies or third-party
        advertising cookies.
      </p>

      <h2 style={h2Style}>8. Third-Party Services</h2>
      <p>
        The Service integrates with WhatsApp and Telegram. Your use of those
        platforms is governed by their respective privacy policies. We encourage
        you to review them.
      </p>

      <h2 style={h2Style}>9. Children's Privacy</h2>
      <p>
        The Service is not intended for children under 18. We do not knowingly
        collect personal information from children.
      </p>

      <h2 style={h2Style}>10. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will notify you
        of material changes via email or through the Service.
      </p>

      <h2 style={h2Style}>11. Contact</h2>
      <p>
        For privacy-related inquiries, contact us at{" "}
        <a
          href="mailto:support@supergreen.cc"
          style={{ color: "var(--primary)" }}
        >
          support@supergreen.cc
        </a>.
      </p>
    </div>
  </div>
);

export const RefundPolicyPage = () => (
  <div style={pageStyle}>
    <PolicyNav />
    <div style={sectionStyle}>
      <h1 style={h1Style}>Refund Policy</h1>
      <p style={{ opacity: 0.7, fontSize: 13 }}>Last updated: {lastUpdated}</p>

      <h2 style={h2Style}>Eligibility</h2>
      <p>
        If you are not satisfied with the Service, you may request a full refund
        within 14 days of your initial purchase. This applies to the first
        payment only.
      </p>

      <h2 style={h2Style}>How to Request a Refund</h2>
      <p>
        To request a refund, contact us at{" "}
        <a
          href="mailto:support@supergreen.cc"
          style={{ color: "var(--primary)" }}
        >
          support@supergreen.cc
        </a>{" "}
        with your account email and the reason for the refund. We will process
        eligible refunds within 5-10 business days.
      </p>

      <h2 style={h2Style}>After 14 Days</h2>
      <p>
        After the 14-day refund window, payments are non-refundable. You may
        cancel your subscription at any time, and your access will continue
        until the end of the current billing period.
      </p>

      <h2 style={h2Style}>Free Trial</h2>
      <p>
        If you cancel during the free trial period, you will not be charged. No
        refund is needed as no payment has been collected.
      </p>

      <h2 style={h2Style}>Processing</h2>
      <p>
        Refunds are processed through Stripe, our payment provider. The refund
        will be returned to the original payment method. Processing times may
        vary depending on your bank or payment provider.
      </p>
    </div>
  </div>
);

export const LegalPage = () => (
  <div style={pageStyle}>
    <PolicyNav />
    <p>
      Please see our individual policies:
    </p>
    <ul style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <li>
        <a href="/terms" style={{ color: "var(--primary)" }}>
          Terms of Service
        </a>
      </li>
      <li>
        <a href="/privacy" style={{ color: "var(--primary)" }}>
          Privacy Policy
        </a>
      </li>
      <li>
        <a href="/refund-policy" style={{ color: "var(--primary)" }}>
          Refund Policy
        </a>
      </li>
    </ul>
  </div>
);
